import * as React from 'react';

type LocationState = {
  pathname: string;
  search: string;
  hash: string;
};

type NavigateOptions = {
  replace?: boolean;
};

type RouterValue = {
  location: LocationState;
  navigate: (to: string, options?: NavigateOptions) => void;
};

const RouterContext = React.createContext<RouterValue | null>(null);

function readLocation(): LocationState {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

function safeInternalPath(to: string) {
  if (!to.startsWith('/') || to.startsWith('//') || /[\r\n\\]/.test(to)) return '/';
  return to;
}

export function BrowserRouter({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = React.useState(readLocation);

  React.useEffect(() => {
    const update = () => setLocation(readLocation());
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  const navigate = React.useCallback((to: string, options: NavigateOptions = {}) => {
    const path = safeInternalPath(to);
    if (options.replace) window.history.replaceState(null, '', path);
    else window.history.pushState(null, '', path);
    setLocation(readLocation());
  }, []);

  return <RouterContext.Provider value={{ location, navigate }}>{children}</RouterContext.Provider>;
}

function useRouter() {
  const router = React.useContext(RouterContext);
  if (!router) throw new Error('La navegación debe estar dentro de BrowserRouter');
  return router;
}

export function useLocation() {
  return useRouter().location;
}

export function useNavigate() {
  return useRouter().navigate;
}

type LinkProps = Omit<React.ComponentProps<'a'>, 'href'> & { to: string };

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, onClick, target, ...props },
  ref,
) {
  const { navigate } = useRouter();
  const href = safeInternalPath(to);
  return (
    <a
      ref={ref}
      href={href}
      target={target}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          (target && target !== '_self')
        ) return;
        event.preventDefault();
        navigate(href);
      }}
      {...props}
    />
  );
});

type NavLinkProps = Omit<LinkProps, 'className'> & {
  className?: string | ((state: { isActive: boolean }) => string);
};

export const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { className, to, ...props },
  ref,
) {
  const { pathname } = useLocation();
  const destination = safeInternalPath(to).split(/[?#]/)[0];
  const isActive = destination === '/' ? pathname === '/' : pathname === destination || pathname.startsWith(`${destination}/`);
  return <Link ref={ref} to={to} className={typeof className === 'function' ? className({ isActive }) : className} {...props} />;
});

export function Navigate({ to, replace = false }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  React.useEffect(() => navigate(to, { replace }), [navigate, replace, to]);
  return null;
}

export function useSearchParams(): [
  URLSearchParams,
  (next: URLSearchParams | Record<string, string>, options?: NavigateOptions) => void,
] {
  const location = useLocation();
  const navigate = useNavigate();
  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const setParams = React.useCallback(
    (next: URLSearchParams | Record<string, string>, options?: NavigateOptions) => {
      const query = next instanceof URLSearchParams ? next : new URLSearchParams(next);
      navigate(`${location.pathname}${query.size ? `?${query}` : ''}`, options);
    },
    [location.pathname, navigate],
  );
  return [params, setParams];
}

export function useParams(): Record<string, string | undefined> {
  const { pathname } = useLocation();
  const invitation = pathname.match(/^\/invitacion\/([^/]+)\/?$/);
  return invitation ? { code: decodeURIComponent(invitation[1]) } : {};
}
